import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { IconButtonComponent } from '@libs/common/components';
import { InputListModel } from '@libs/form/models';
import { DynamicElementsDirective } from '../../../directives';
import { DYNAMIC_ELEMENTS_MAP, GroupBaseComponent } from '../../../models';
import { InputListService } from '../../../services';
import { InputItemsMap } from '../../input';

@Component({
  selector: 'ui-input-list-table',
  templateUrl: './input-list-table.component.html',
  styleUrls: ['./input-list-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DynamicElementsDirective,
    ReactiveFormsModule,
    MatButtonModule,
    IconButtonComponent,
  ],
  providers: [
    InputListService,
    {
      provide: DYNAMIC_ELEMENTS_MAP,
      useValue: InputItemsMap,
    },
  ],
  animations: [
    trigger('rowAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(-86%)',
        }),
        animate('300ms ease-out', style({
          opacity: 1,
          transform: 'translateY(0)',
        })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({
          opacity: 0,
          transform: 'translateY(-86%)',
        })),
      ]),
    ]),
  ],
})
export class InputListTableComponent extends GroupBaseComponent implements OnInit, OnChanges {
  private readonly inputListService: InputListService = inject(InputListService);

  @Input() public override data!: InputListModel;

  public inputRows: InputListModel[] = [];
  public inputListTemplate!: InputListModel;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.data = this.inputListService.normalizeInputListData(this.data);
      this.inputListTemplate = this.inputListService.buildInputListTemplate(this.data);
    }
  }

  public ngOnInit(): void {
    this.addRow(-1);
  }

  public addRow(index: number): void {
    if (index === -1 && this.inputRows.length === 0) {
      this.inputRows.splice(index + 1, 0, this.data);
    } else {
      this.inputRows.splice(index + 1, 0, this.inputListTemplate);
    }

    // Shift form controls to make space for the new input row
    this.inputListService.shiftControlsForInsert(this.control, this.inputRows.length, index + 1);

    // Add a new empty control for the new input row
    this.inputListService.addRowControl(this.control, index + 1, this.data);
  }

  public removeRow(index: number): void {
    if (this.inputRows.length === 1) {
      return;
    }

    this.inputListService.removeRowControl(this.control, this.inputRows.length, index);
    this.inputRows.pop();
  }

  public getGroupControl(index: number): FormGroup {
    return this.control.get(index.toString()) as FormGroup;
  }
}
